import React from "react";
import { BlockMath, InlineMath } from "react-katex";
import ABCDInput from "../../../../components/inputs/ABCDInput";

const LETTERS = ["A", "B", "C", "D"];
const VARIANTS = [
  { leftIndex: 1, middleIndex: 5, rightIndex: 9, leftValue: 1, middleValue: 17, rightValue: 33 },
  { leftIndex: 2, middleIndex: 6, rightIndex: 10, leftValue: 2, middleValue: 14, rightValue: 26 },
  { leftIndex: 3, middleIndex: 6, rightIndex: 9, leftValue: -3, middleValue: 9, rightValue: 21 },
  { leftIndex: 2, middleIndex: 5, rightIndex: 8, leftValue: 5, middleValue: 20, rightValue: 35 },
  { leftIndex: 4, middleIndex: 7, rightIndex: 10, leftValue: -4, middleValue: 8, rightValue: 20 },
  { leftIndex: 1, middleIndex: 4, rightIndex: 7, leftValue: 3, middleValue: 12, rightValue: 21 },
  { leftIndex: 3, middleIndex: 7, rightIndex: 11, leftValue: 7, middleValue: 23, rightValue: 39 },
  { leftIndex: 2, middleIndex: 5, rightIndex: 8, leftValue: 0, middleValue: 18, rightValue: 36 },
];

const pick = (items) => items[Math.floor(Math.random() * items.length)];
const shuffle = (items) => [...items].sort(() => Math.random() - 0.5);
const term = (index) => `a_{${index}}`;

export function generateTask(options = {}) {
  const original = options.variant === "original" && options.session === "2026_MAJ";
  const data = original ? VARIANTS[0] : pick(VARIANTS);
  const increment = data.middleValue - data.leftValue;
  const choices = shuffle([
    data.rightValue,
    data.rightValue - data.rightIndex + data.middleIndex,
    data.rightValue + data.rightIndex - data.middleIndex,
    data.middleValue,
  ]);
  const taskOptions = choices.map((value, index) => ({
    value,
    latex: String(value),
    label: LETTERS[index],
    isCorrect: value === data.rightValue,
  }));
  return {
    type: "MaturaArithmeticSymmetricTermClosedTask",
    data: {
      ...data,
      increment,
      options: taskOptions,
      correctIndex: taskOptions.findIndex((item) => item.isCorrect),
    },
    source: {
      variant: original ? "original" : "random",
      exam: "MATURA",
      session: original ? "2026_MAJ" : undefined,
      taskNumber: "16",
      basedOn: "MMAP-P0-100-A-2605-16",
    },
  };
}

export function renderTask({ data }) {
  return (
    <div>
      <p>Ciąg <InlineMath math="(a_n)" /> jest arytmetyczny. Dane są:</p>
      <BlockMath math={`${term(data.leftIndex)}=${data.leftValue},\\qquad ${term(data.middleIndex)}=${data.middleValue}.`} />
      <p>Wyraz <InlineMath math={term(data.rightIndex)} /> jest równy:</p>
      {data.options.map((option) => <div key={option.label}><InlineMath math={`${option.label}.\\;${option.latex}`} /></div>)}
    </div>
  );
}

export function renderInput({ data }, value = {}, onChange, disabled) {
  return <ABCDInput options={data.options.map((option, index) => ({ value: index, label: option.label, textNode: option.label }))} value={value?.choiceIndex ?? null} disabled={disabled} onChange={(index) => onChange?.({ choiceIndex: index, label: data.options[index].label, value: data.options[index].value })} />;
}

export function checkAnswer({ data }, value = {}) {
  return value?.choiceIndex === data.correctIndex;
}

export function renderAnswer({ data }) {
  const correct = data.options[data.correctIndex];
  return <InlineMath math={`${correct.label}.\\;${correct.latex}`} />;
}

export function renderUserAnswer(value = {}) {
  if (value?.value == null) return <i>(brak odpowiedzi)</i>;
  return <InlineMath math={`${value.label}.\\;${value.value}`} />;
}

export function buildExplanationModel(data) {
  const indexStep = data.middleIndex - data.leftIndex;
  return {
    equalDistance: `${data.middleIndex}-${data.leftIndex}=${data.rightIndex}-${data.middleIndex}=${indexStep}`,
    increment: `${term(data.middleIndex)}-${term(data.leftIndex)}=${data.middleValue}-(${data.leftValue})=${data.increment}`,
    nextTerm: `${term(data.rightIndex)}=${term(data.middleIndex)}+${data.increment}=${data.middleValue}+${data.increment}=${data.rightValue}`,
  };
}

export function renderExplanation({ data }) {
  const model = buildExplanationModel(data);
  return (
    <div style={{ display: "grid", gap: 12 }}>
      <p><strong>Krok 1.</strong> Numery podanych i szukanego wyrazu są jednakowo oddalone.</p>
      <BlockMath math={model.equalDistance} />

      <p><strong>Krok 2.</strong> W ciągu arytmetycznym przejściu o tę samą liczbę miejsc odpowiada taki sam przyrost wartości. Obliczamy przyrost od pierwszego podanego wyrazu do środkowego.</p>
      <BlockMath math={model.increment} />

      <p><strong>Krok 3.</strong> Ten sam przyrost dodajemy do wyrazu środkowego.</p>
      <BlockMath math={model.nextTerm} />

      <p><strong>Wniosek.</strong> Szukany wyraz to <InlineMath math={`${term(data.rightIndex)}=${data.rightValue}`} />.</p>
    </div>
  );
}
